export const NOTICE_DISCLOSURE_STORAGE_KEY =
  "fishystuff.notice-disclosure.v1";
export const RETIRED_UI_SETTINGS_STORAGE_KEY =
  "fishystuff.ui.settings.v1";

const RETIRED_NOTICE_PATHS = Object.freeze([
  Object.freeze({
    noticeId: "guide-translation-help",
    path: Object.freeze(["guides", "noticeOpen"]),
  }),
  Object.freeze({
    noticeId: "calculator-data-quality",
    path: Object.freeze(["calculator", "noticeOpen"]),
  }),
  Object.freeze({
    noticeId: "map-zone-pane",
    path: Object.freeze(["map", "zonePane", "noticeOpen"]),
  }),
]);
const MAX_RETIRED_SETTINGS_CHARS = 65_536;

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function result(kind, fields = {}) {
  return Object.freeze({ kind, ...fields });
}

function removePath(root, path) {
  if (!isRecord(root) || path.length === 0 || !Object.hasOwn(root, path[0])) {
    return root;
  }
  const next = { ...root };
  if (path.length === 1) {
    delete next[path[0]];
    return next;
  }
  const child = removePath(next[path[0]], path.slice(1));
  if (isRecord(child) && Object.keys(child).length > 0) {
    next[path[0]] = child;
  } else {
    delete next[path[0]];
  }
  return next;
}

function decodeRetiredSettings(raw) {
  if (typeof raw !== "string") {
    return result("invalid", {
      reason: "retired-ui-settings-must-be-text",
      raw,
    });
  }
  if (raw.length === 0 || raw.length > MAX_RETIRED_SETTINGS_CHARS) {
    return result("invalid", {
      reason: raw.length === 0
        ? "retired-ui-settings-must-not-be-empty"
        : "retired-ui-settings-exceeds-import-limit",
      raw,
    });
  }
  try {
    const parsed = JSON.parse(raw);
    return isRecord(parsed)
      ? result("ready", { settings: parsed })
      : result("invalid", {
        reason: "retired-ui-settings-must-be-an-object",
        raw,
      });
  } catch (_error) {
    return result("invalid", {
      reason: "retired-ui-settings-is-malformed-json",
      raw,
    });
  }
}

function valueAtPath(root, path) {
  let current = root;
  for (const part of path) {
    if (!isRecord(current) || !Object.hasOwn(current, part)) {
      return { found: false, value: undefined };
    }
    current = current[part];
  }
  return { found: true, value: current };
}

function retiredDisclosures(settings) {
  const disclosures = {};
  const rawValues = {};
  const invalidIds = [];
  for (const entry of RETIRED_NOTICE_PATHS) {
    const candidate = valueAtPath(settings, entry.path);
    if (!candidate.found) continue;
    rawValues[entry.noticeId] = candidate.value;
    if (typeof candidate.value !== "boolean") {
      invalidIds.push(entry.noticeId);
    } else {
      disclosures[entry.noticeId] = candidate.value;
    }
  }
  if (invalidIds.length > 0) {
    return result("invalid", {
      reason: "retired-notice-values-must-be-boolean",
      invalidIds: Object.freeze(invalidIds),
      raw: JSON.stringify(rawValues),
    });
  }
  if (Object.keys(disclosures).length === 0) return result("missing");
  return result("ready", {
    disclosures: Object.freeze(disclosures),
    raw: JSON.stringify(rawValues),
  });
}

export function createNoticeDisclosureLocalProvider({
  storage,
  eventTarget = null,
} = {}) {
  let disposed = false;
  const invalidationListeners = new Set();

  function unavailable(operation) {
    return result("unavailable", {
      operation,
      reason: `storage-${operation}-failed`,
    });
  }

  function readKey(key) {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return result("ok", { raw: storage.getItem(key) });
    } catch (_error) {
      return unavailable("read");
    }
  }

  function writeKey(key, raw) {
    try {
      if (!storage || typeof storage.setItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(key, raw);
      if (storage.getItem(key) !== raw) {
        throw new Error("Storage write was not acknowledged.");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("write");
    }
  }

  function removeKey(key) {
    try {
      if (!storage || typeof storage.removeItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(key);
      if (storage.getItem(key) !== null) {
        throw new Error("Storage removal was not acknowledged.");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("remove");
    }
  }

  function readDocument() {
    return readKey(NOTICE_DISCLOSURE_STORAGE_KEY);
  }

  function writeDocument(raw) {
    return writeKey(NOTICE_DISCLOSURE_STORAGE_KEY, raw);
  }

  function removeDocument() {
    return removeKey(NOTICE_DISCLOSURE_STORAGE_KEY);
  }

  function readRetiredDisclosures() {
    const stored = readKey(RETIRED_UI_SETTINGS_STORAGE_KEY);
    if (stored.kind === "unavailable") return stored;
    if (stored.raw === null) return result("missing");
    const decoded = decodeRetiredSettings(stored.raw);
    return decoded.kind === "ready"
      ? retiredDisclosures(decoded.settings)
      : decoded;
  }

  function removeRetiredDisclosures() {
    const stored = readKey(RETIRED_UI_SETTINGS_STORAGE_KEY);
    if (stored.kind === "unavailable") return stored;
    if (stored.raw === null) return result("acknowledged");
    const decoded = decodeRetiredSettings(stored.raw);
    if (decoded.kind === "invalid") {
      return result("unavailable", {
        operation: "remove",
        reason: decoded.reason,
      });
    }
    const settings = decoded.settings;
    if (retiredDisclosures(settings).kind === "missing") {
      return result("acknowledged");
    }
    const next = RETIRED_NOTICE_PATHS.reduce(
      (current, entry) => removePath(current, entry.path),
      settings,
    );
    if (Object.keys(next).length === 0) {
      return removeKey(RETIRED_UI_SETTINGS_STORAGE_KEY);
    }
    return writeKey(RETIRED_UI_SETTINGS_STORAGE_KEY, JSON.stringify(next));
  }

  function notifyInvalidation() {
    if (disposed) return;
    for (const listener of invalidationListeners) {
      try {
        listener();
      } catch (error) {
        globalThis.reportError?.(error);
      }
    }
  }

  function handleStorage(event) {
    if (
      event?.key === null
      || event?.key === NOTICE_DISCLOSURE_STORAGE_KEY
      || event?.key === RETIRED_UI_SETTINGS_STORAGE_KEY
    ) {
      notifyInvalidation();
    }
  }

  function handlePageShow(event) {
    if (event?.persisted === true) notifyInvalidation();
  }

  eventTarget?.addEventListener?.("storage", handleStorage);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);

  function subscribeInvalidation(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Notice disclosure invalidation listener must be a function.");
    }
    if (disposed) return () => {};
    invalidationListeners.add(listener);
    return () => invalidationListeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    invalidationListeners.clear();
    eventTarget?.removeEventListener?.("storage", handleStorage);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  }

  return Object.freeze({
    dispose,
    readDocument,
    readRetiredDisclosures,
    removeDocument,
    removeRetiredDisclosures,
    subscribeInvalidation,
    writeDocument,
  });
}

export function createBrowserNoticeDisclosureLocalProvider() {
  let storage = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch (_error) {
    storage = null;
  }
  return createNoticeDisclosureLocalProvider({
    storage,
    eventTarget: globalThis.window ?? null,
  });
}
